import { Filtering } from '../../service/filtering/filtering';
import { RequestHandler } from '../../service/OffService/request-handler';
import type {
    DropdownData,
    FilteredValue,
    Value,
} from '@fitogest/reactive-form-builder/interfaces';

export type BuildTask = (resolve: (ok: boolean) => void) => void;

export type DropdownListItem<T> = T | [string, T | null];

type DropdownDataLike = DropdownData<any>;

type DropdownLabelSelector<T> = keyof T | readonly (keyof T)[] | ((item: T) => unknown) | { or: readonly (keyof T)[] };

const DEFAULT_REQUEST_TIMEOUT_MS = 30000;

function isDropdownFieldList<T>(field: DropdownLabelSelector<T>): field is readonly (keyof T)[] {
    return Array.isArray(field);
}

function isDropdownOrSelector<T>(field: DropdownLabelSelector<T>): field is { or: readonly (keyof T)[] } {
    return typeof field === 'object' && field !== null && 'or' in field;
}

function toDropdownOption<T>(item: DropdownListItem<T>): FilteredValue<T> {
    if (Array.isArray(item)) {
        return {
            label: item[0],
            value: item[1],
            raw: item[1],
            valueAliases: getDropdownAliases(item[1], item[0]),
        };
    }

    return {
        label: String(item),
        value: item,
        raw: item,
        valueAliases: getDropdownAliases(item, String(item)),
    };
}

function getDropdownAliases<T>(item: T | null, label: string): readonly unknown[] {
    if (item === null || item === undefined || typeof item !== 'object') {
        return [label].filter((value) => String(value ?? '').trim() !== '');
    }

    const record = item as Record<string, unknown>;
    const aliases = [
        record['id'],
        record['codigo'],
        record['cod_producto'],
        record['codigo_producto'],
        record['nombre'],
        record['razon_social'],
        record['descripcion'],
        label,
    ];

    return aliases.filter((value) => value !== null && value !== undefined && String(value).trim() !== '');
}

function readDropdownField<T>(item: T, field: keyof T | undefined): unknown {
    if (field === undefined) {
        return undefined;
    }

    return (item as Record<PropertyKey, unknown>)[field as PropertyKey];
}

function getDropdownLabel<T>(item: T, field: DropdownLabelSelector<T>): string {
    if (typeof field === 'function') {
        return String(field(item) ?? '');
    }

    if (isDropdownFieldList(field)) {
        return field.map((fieldName) => readDropdownField(item, fieldName)).join(' ');
    }

    if (isDropdownOrSelector(field)) {
        const [firstField, secondField] = field.or;

        return String(readDropdownField(item, firstField) || readDropdownField(item, secondField) || '');
    }

    return String(readDropdownField(item, field) ?? '');
}

export function baseSentence<T>(
    data: T[],
    field: DropdownLabelSelector<T>,
    noPlaceholder?: boolean,
): FilteredValue<T>[] {
    const distinctByField = new Set<string>();
    const placeholder: FilteredValue<T>[] = noPlaceholder ? [] : [{label: '...', value: null}];
    const source = Array.isArray(data) ? data : [];
    const options = source
        .filter((item): item is T => item !== null && typeof item === 'object')
        .filter((item) => {
            const label = getDropdownLabel(item, field);

            if (distinctByField.has(label)) {
                // Exception for 'SIN VARIEDAD' to avoid filtering it out when repeated as a domain sentinel.
                return label === 'SIN VARIEDAD';
            }

            distinctByField.add(label);
            return true;
        })
        .map((item) => {
            const label = getDropdownLabel(item, field);

            return {
                label,
                value: item,
                raw: item,
                valueAliases: getDropdownAliases(item, label),
            };
        })
        .sort(Filtering.sort('label', 1));

    return placeholder.concat(options);
}

export function filterSentence(data: any[], field: string): any[] {
    return [{label: '...', value: null}]
        .concat(
            (data instanceof Array ? data : [])
                .filter(it => it instanceof Object)
                .sort(Filtering.sort(field, 1))
                .map(it => ({label: it[field], value: it}))
        );
}


export function baseRequest<T>(request: RequestHandler<T>, params?: any, timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS): Promise<any> {
    return new Promise<any>((resolve, reject) => {
        request.unsuscribe();

        let isSettled = false;
        const timeout = globalThis.setTimeout(() => {
            if (isSettled) {
                return;
            }

            isSettled = true;
            request.unsuscribe();
            reject(new Error('Request timeout'));
        }, timeoutMs);

        request.response((data: T) => {
            if (isSettled) {
                return;
            }

            isSettled = true;
            globalThis.clearTimeout(timeout);
            request.unsuscribe();
            resolve(data);
        });

        try {
            request.safePerform(params);
        } catch (error: unknown) {
            if (isSettled) {
                return;
            }

            isSettled = true;
            globalThis.clearTimeout(timeout);
            request.unsuscribe();
            reject(error instanceof Error ? error : new Error(String(error)));
        }
    });
}

export function list<T = any>(...values: DropdownListItem<T>[]): DropdownData<T> {
    const filtered: FilteredValue<T>[] = values.length > 0
        ? values.map(toDropdownOption)
        : [{label: '...', value: null}];

    return {
        filtered,
        values: [],
    };
}

export function filterList<T = any>(...values: DropdownListItem<T>[]): DropdownData<T> {
    const listValues: Value<T>[] = values.length > 0
        ? values.map(toDropdownOption)
        : [{label: 'Cargando...', value: null}];

    return {
        filtered: [],
        values: listValues,
    };
}

export function build<T>(
    request: RequestHandler<T> | RequestHandler<T>[],
    object: DropdownDataLike | DropdownDataLike[],
    label: any,
    _filtrable?: boolean,
    noPlaceholder?: boolean
): BuildTask {
    return (resolve: (resolved: boolean) => void) => {
        const requests = Array.isArray(request) ? request : [request];

        Promise.all(requests.map(r => baseRequest(r)))
            .then(results => {
                // Combine all request results into a single dropdown source.
                const combined = ([] as T[]).concat(...results);

                const data = baseSentence(combined, label, noPlaceholder);

                const assignData = (obj: DropdownDataLike) => {
                    const existingValues = obj.values ?? [];
                    const getId = (entry: any) => entry?.value?.id;

                    const newIds = new Set(data.map(getId));
                    const remainingItems = existingValues.filter(item => !newIds.has(getId(item)));

                    const combinedValues = [...data, ...remainingItems];

                    obj.filtered = [...combinedValues];
                    obj.values = [...combinedValues];
                };

                if (Array.isArray(object)) {
                    object.filter(it => it).forEach(assignData);
                } else {
                    assignData(object);
                }

                resolve(true);
            })
            .catch((err: unknown) => {
                console.error('Error loading dropdown data:', err);
                resolve(false);
            });
    };
}


export function buildParams<T>(
    request: RequestHandler<T>,
    params: any,
    object: DropdownDataLike | DropdownDataLike[],
    label: string | string[],
    _filtrable?: boolean,
    noPlaceholder?: boolean
): (resolve: (resolved: boolean) => void) => void {
    return (resolve: (resolved: boolean) => void) => {
        baseRequest(request, params)
            .then(data => baseSentence(data, label as DropdownLabelSelector<any>, noPlaceholder))
            .then(data => {
                if (object instanceof Array) {
                    object.filter(it => it)
                        .forEach(obj => {
                            obj['filtered'] = (data || []).slice();
                            obj['values'] = (data || []).slice();
                        });
                } else {
                    object['filtered'] = data;
                    object['values'] = data;
                }
                return;
            })
            .then(() => {
                resolve(true);
                return;
            }).catch (e => {
                console.error('Error loading dropdown params:', e);
                resolve(false);
            }
            );
    };
}

export function stack(queue: any[], callback?: Function): Promise<any> {
    return new Promise(queue[0] || ((resolve) => resolve(null)))
        .then((next) => {
            if (callback) {
                callback();
            }

            if (next === true) {
                (queue || []).shift();
                if ((queue || []).length > 0) {
                    return stack(queue, callback);
                } else {
                    return new Promise(resolve => resolve(next)).then(() => next);
                }
            } else if (next === false) {
                return new Promise(resolve => resolve(next)).then(() => next);
            } else {
                return new Promise(resolve => resolve(next)).then(() => true);
            }
        });
}

export function pool(queue: any[], callback?: Function): Promise<any> {
    return new Promise((resolve) => {
        const expected = (queue || []).length;

        if (expected === 0) {
            resolve(0);
            return;
        }

        let processed = 0;

        const checkHandlers = () => {
            if (callback) {
                callback();
            }
            processed++;
            if (expected === processed) {
                resolve(processed);
            }
        };

        (queue || []).forEach(rq => {
            new Promise(rq).then(checkHandlers).catch (e => {
                console.error('Error processing request pool item:', e);
                checkHandlers();
            }
            );
        });
    });
}

export function promiseMeanful(name: string, action: string): Promise<unknown> {
    console.log(name);
    console.log(action);
    return new Promise((resolve) => resolve);
}

export function promise(): Promise<unknown> {
    return new Promise((resolve) => resolve);
}

